import React, { useEffect, useMemo, useState, memo } from "react";
import { Tables, Typography, showToast } from "@deeptrue-frontend/ui";
import Search from "components/Fields/Search";
import api from "api";
import { UserType } from "pages/OrganizationAdmin/Users";
import User from "./User";
import { ProjectType } from "pages/OrganizationAdmin/types";
import { UserShared } from "pages/OrganizationAdmin/types";
import { useFormatMessage } from "components/IntlProvider";

const { Table, TableBody, TableHeader } = Tables;

interface Props {
  projectData: ProjectType;
  isLoaded: boolean;
  organizationCodename: string;
  refreshProject: () => void;
}

const Users = memo(
  ({ projectData, organizationCodename, isLoaded, refreshProject }: Props) => {
    const [searchedUsers, setSearchedUsers] = useState<UserType[]>([]);
    const [projectUsers, setProjectUsers] = useState<UserShared[]>([]);
    const t = useFormatMessage();

    const getUsers = (searchValue?: string) => {
      return api.resources.organization.users
        .getOrganizationUsers({
          organizationCodename: organizationCodename,
          params: {
            search: searchValue,
          },
        })
        .then(({ data }) => {
          setSearchedUsers(data.results);
        });
    };

    useEffect(() => {
      getUsers();
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      setProjectUsers(projectData.shared_to_users);
    }, [projectData.shared_to_users]);

    const addUserToProject = (id: number) => {
      return api.resources.organization.projects
        .shareProjectWithUser({
          organizationCodename: organizationCodename,
          id: projectData.id as number,
          userId: id,
        })
        .then(() => {
          refreshProject();
          showToast({
            type: "success",
            title: t("organization.addedTo", "{x} added to {y}", {
              x: t("organization.user", "User"),
              y: t("organization.project", "Project"),
            }),
          });
        });
    };

    const removeUserfromProject = (id: number) => {
      return api.resources.organization.projects
        .unshareProjectWithUser({
          organizationCodename: organizationCodename,
          id: projectData.id as number,
          userId: id,
        })
        .then(() => {
          refreshProject();
          showToast({
            type: "success",
            title: t("organization.removedFrom", "{x} removed from {y}", {
              x: t("organization.user", "User"),
              y: t("organization.project", "Project"),
            }),
          });
        });
    };

    const options = searchedUsers.map((userData: UserType) => ({
      id: userData.user.id,
      label: `${userData.user.first_name} ${userData.user.last_name} (${userData.user.email})`,
    }));

    const filteredOptions = useMemo(() => {
      return options.filter((searchedUser: { id: number; label: string }) => {
        const itemIndex = projectUsers.findIndex((user) => {
          return user.id === searchedUser.id;
        });

        return itemIndex === -1;
      });
    }, [projectUsers, options]);

    return (
      <>
        <Typography fontWeight={600} mb="1rem">
          {t("organization.addTo", "Add {x} to {y}", {
            y: t("organization.project", "Project"),
            x: t("organization.users", "Users"),
          })}
        </Typography>
        <Search
          options={filteredOptions.map(({ id, label }) => ({
            value: id,
            label,
          }))}
          styles={{
            maxWidth: "450px",
          }}
          name="user"
          onChange={(_name, user) => {
            if (user) {
              addUserToProject(user.value);
            }
          }}
          placeholder={t("organization.searchValue", "Search {name}", {
            name: t("organization.users", "Users"),
          })}
          asyncSeach={(searchedValue: string) => getUsers(searchedValue)}
        />
        <Typography fontWeight={600} mb="1rem" mt="2rem">
          {t("organization.sharedTo", "{x} shared to {y}", {
            x: t("organization.project", "Project"),
            y: t("organization.users", "Users"),
          })}
          :
        </Typography>
        <Table>
          <TableHeader
            cells={[
              {
                text: t("organization.columns.name", "Name"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: "Email",
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: t("organization.columns.resources", "Resources"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
            ]}
          />

          <TableBody
            isLoading={!isLoaded}
            withLoader
            isData={projectData.shared_to_users.length > 0}
            noDataText={t(
              "noresults",
              "No results. We have nothing to show here."
            )}
          >
            {projectUsers.map((user: UserShared) => {
              return (
                <User
                  id={user.id}
                  displayName={`${user.first_name} ${user.last_name}`}
                  email={user.email}
                  key={user.email}
                  removeUser={removeUserfromProject}
                />
              );
            })}
          </TableBody>
        </Table>
      </>
    );
  }
);

export default Users;
